import Vue from 'vue'
import Vuex from 'vuex'
import { ROUTE_PERM_MAP, MENU_ORDER } from '@/config/menuPermissions'

Vue.use(Vuex)

const nowStr = () => new Date().toISOString().slice(0, 19).replace('T', ' ')

// ===== T-011: 登录态本地持久化 (避免刷新页面就被踢回登录) =====
// 只存"恢复会话最少必要字段", 不存敏感数据 (密码已经在 SET_LOGIN_INFO 之外, 这里不会接触)
// 与 localStorage.token 配套: 退出时一并清掉
const AUTH_STORAGE_KEY = 'drying_auth_snapshot_v1'
function persistAuthSnapshot(state) {
  try {
    const payload = {
      loginUser: state.loginUser,
      externalOrg: state.externalOrg,
      menus: state.menus,
      permKeys: state.permKeys,
      authButtons: state.authButtons,
      factoryName: state.factoryName,
      username: state.username
    }
    localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(payload))
  } catch (e) {
    // 写失败 (空间满 / 隐私模式) 不致命, 下次刷新会走 getUserPermissionByToken 兜底
    // eslint-disable-next-line no-console
    console.warn('[store] persistAuthSnapshot failed', e)
  }
}
function loadAuthSnapshot() {
  try {
    const raw = localStorage.getItem(AUTH_STORAGE_KEY)
    if (!raw) return null
    return JSON.parse(raw)
  } catch (e) { return null }
}
function clearAuthSnapshot() {
  try { localStorage.removeItem(AUTH_STORAGE_KEY) } catch (e) {}
}
// 启动时 hydrate (只在 token 还在的情况下恢复, 否则 token 都没了恢复也没意义)
const _savedAuth = (typeof localStorage !== 'undefined' && localStorage.getItem('token'))
  ? (loadAuthSnapshot() || {}) : {}

// [2026-08-10] 地磅实时值通过 weighbridgeMixin 获取（双轨：Web Serial 模式来自 WeighbridgeReader 回调，
//   jinglunGo 模式来自 scaleClient WebSocket 推送），统一写入 store.deviceStatus

export default new Vuex.Store({
  state: {
    factoryName: _savedAuth.factoryName || '',
    username: _savedAuth.username || '',
    currentUser: null,                     // 当前登录用户(含 roleIds / permissions)
    // ===== 真实登录态 (T-007 新增, T-011 加 hydrate) =====
    loginUser: _savedAuth.loginUser || null,
    externalOrg: _savedAuth.externalOrg || null,
    permissions: [],                       // 兼容字段, 早期占位 (保留以免破坏旧逻辑)
    menus: Array.isArray(_savedAuth.menus) ? _savedAuth.menus : [],
    permKeys: Array.isArray(_savedAuth.permKeys) ? _savedAuth.permKeys : [],
    authButtons: Array.isArray(_savedAuth.authButtons) ? _savedAuth.authButtons : [],
    forceChangePwd: false,                 // 是否需要强制改密 (登录后此值为 true 时, 不能进入业务页)
    // 系统类数据 (原 mock 初始化已清除, 待对应页面接入接口)
    dictTypes: [],
    dictItems: [],
    loginLogs: [],
    farmers: [],
    entries: [],
    exits: [],
    seedSales: [],
    payments: [],
    grainTypes: [],
    seedTypes: [],
    customers: [],
    // [2026-05-30] 海康监控 portal: 当前活跃页的摄像头占位 rect (由 EntryV1/Exit 上报),
    // MainLayout 用它对齐共享 HikvisionPreviewGrid; null 表示当前页不需要摄像头
    cameraPortalRect: null,
    deviceStatus: {
      entryScale: { connected: false, model: '', capacity: '', currentWeight: 0, stable: false },
      exitScale: { connected: false, model: '', capacity: '', currentWeight: 0, stable: false },
      // [单次过磅] 点"使用此读数"填入哪个字段: 'gross'=毛重 / 'tare'=皮重. 由 header 切换按钮控制.
      //   仅单次过磅(once)模式生效; 两次过磅由流程阶段固定, 不受此值影响.
      useScaleTarget: 'gross',
      // [2026-07-31] 磅启用状态 (过磅设备配置页可关闭大磅/小磅). 默认两个都启用.
      //   由 GlobalScaleBar 从 localStorage 水合 + 配置页保存时更新, 供顶栏/过磅卡片/弹窗响应式显隐.
      scaleEnabled: { entry: true, exit: true },
      // [2026-10-11] 本机外设服务(127.0.0.1:8765)在线状态. null=未知(刚加载未探测),
      //   true/false 由 MainLayout 的 peripheralHealth 轮询写入. 离线时顶栏地磅条出红徽标 +
      //   双磅 connected 被斩断 (防冻结旧读数被"使用此读数"录进单据).
      serviceOnline: null,
      // [2026-08-14] 本机无外设硬件总开关 (过磅设备配置页). 默认 false=使用硬件.
      //   开启后顶栏地磅条隐藏、不订阅地磅 WS/不自动连接; 由 GlobalScaleBar 水合 + 配置页保存时更新,
      //   改动即时生效 (watch 断开/恢复连接), 不需要刷新页面.
      hardwareDisabled: false,
      // [2026-09-04] UPort 驱动重装弹窗显隐 (全局单例, 挂在 MainLayout).
      //   触发方: ①地磅多次连接失败的大提示 ②驱动管理页快捷按钮 ③设备配置页.
      uportReinstallVisible: false,
      rfid: { connected: false, model: '' },
      qr: { connected: false, model: '' },
      cameras: [],
      printer: { connected: false, model: '' }
    },
    syncStatus: {
      lastSyncTime: '',
      pendingRecords: 0,
      networkOk: true
    }
  },
  getters: {
    farmerBalance: state => farmerId => {
      const grainAmount = state.entries
        .filter(e => e.farmerId === farmerId && e.status === 'COMPLETED' && e.paymentStatus === 'UNPAID')
        .reduce((sum, e) => sum + (e.adjustedAmount || 0), 0)
      const seedDebt = state.seedSales
        .filter(s => s.farmerId === farmerId)
        .reduce((sum, s) => sum + (s.totalAmount - s.paidAmount), 0)
      return { grainAmount, seedDebt, netBalance: grainAmount - seedDebt }
    },

    allFarmersWithBalance: (state, getters) => {
      return state.farmers.map(f => ({ ...f, ...getters.farmerBalance(f.id) }))
    },

    farmerLastSeed: state => farmerId => {
      const list = state.seedSales
        .filter(s => s.farmerId === farmerId)
        .sort((a, b) => b.createTime.localeCompare(a.createTime))
      return list[0] || null
    },

    totalPayable: (state, getters) => {
      return getters.allFarmersWithBalance.filter(f => f.netBalance > 0).reduce((s, f) => s + f.netBalance, 0)
    },

    // ===== 系统类 getters =====
    dictItemsByType: state => typeCode =>
      state.dictItems.filter(it => it.typeCode === typeCode).sort((a, b) => a.sort - b.sort),

    // ===== T-010A: 真实权限相关 getters =====
    // 登录后跳到第一个有权的菜单 (按 MENU_ORDER 顺序; 没有则返回空字符串)
    firstAccessibleRoute: state => {
      const keys = Array.isArray(state.permKeys) ? state.permKeys : []
      for (const name of MENU_ORDER) {
        const perm = ROUTE_PERM_MAP[name]
        if (perm && keys.includes(perm)) return '/' + name
      }
      return ''
    },
    // [需求] 地磅重量数据手动编辑权限 — 白名单: 仅烘干厂管理员 (DRY_SUPER_ADMIN) 与系统超管 (SUPPER_ADMIN)
    // 库管员 DRY_KEEPER / 财务 DRY_FINANCE 不能手动改毛重/皮重, 只能用设备读数.
    // 角色来源: 后端 /sys/login 返回的 userInfo.roleCodes (LoginController#login).
    canEditWeigh: state => {
      const u = state.loginUser || {}
      const codes = Array.isArray(u.roleCodes) ? u.roleCodes : []
      return codes.includes('DRY_SUPER_ADMIN') || codes.includes('SUPPER_ADMIN')
    },
    // [需求] 仓位容量编辑权限 — 白名单: 仅烘干厂管理员 (DRY_SUPER_ADMIN) 与系统超管 (SUPPER_ADMIN);
    // 名称人人可改 (同"仓位改名"), 容量只有厂超管能改 (后端 saveOrUpdateWarearea 同步校验).
    canEditWareareaCapacity: state => {
      const u = state.loginUser || {}
      const codes = Array.isArray(u.roleCodes) ? u.roleCodes : []
      return codes.includes('DRY_SUPER_ADMIN') || codes.includes('SUPPER_ADMIN')
    }
  },
  mutations: {
    // ===== T-007: 真实登录信息 (来自后端 /sys/login result) =====
    // jeecg SysUserInfo 字段: loginAcct (登录账号) / userName (真实姓名), 不是 username
    SET_LOGIN_INFO(state, result) {
      if (!result) return
      const u = result.userInfo || {}
      state.loginUser = u
      // username state 保留登录账号 (兼容旧代码取 state.username 的地方)
      state.username = u.loginAcct || u.username || ''
      state.externalOrg = result.externalOrg || null
      state.permissions = Array.isArray(result.permissions) ? result.permissions : []
      // 菜单不再随登录响应返回 —— 登录后异步调 getDryingMenuByToken 接口拿 (见 SET_DRYING_MENUS).
      // 这里只初始化为空, 登录流程 / 路由守卫负责填充.
      state.menus = []
      state.permKeys = []
      state.forceChangePwd = !!result.forceChangePwd
      // 厂名: 外部用户取 externalOrg, 内部用户清空 (避免显示 mock 数据)
      if (result.externalOrg) {
        const fname = result.externalOrg.factoryName || result.externalOrg.saleOrgName
        state.factoryName = fname || ''
      } else if (result.isExternal === false) {
        state.factoryName = '粮易智联 (内部账号)'
      }
      // T-011: 落盘 snapshot, 刷新后能 hydrate 回来
      persistAuthSnapshot(state)
    },

    // ===== 个人中心-修改资料成功后, 局部更新 loginUser (姓名/邮箱) 并落盘 =====
    // payload: { userName?, email? } — 只更新传入的字段
    UPDATE_MY_INFO(state, payload) {
      if (!state.loginUser || !payload) return
      if (payload.userName !== undefined) state.loginUser.userName = payload.userName
      if (payload.email !== undefined) state.loginUser.email = payload.email
      persistAuthSnapshot(state)
    },

    // ===== 菜单异步加载: 由 getDryingMenuByToken 接口返回的 [{id}] 数组填充 permKeys =====
    // payload: [{ id: 'DRY_ENTRY_V1' }, ...]
    SET_DRYING_MENUS(state, menus) {
      const arr = Array.isArray(menus) ? menus : []
      state.menus = arr
      state.permKeys = arr
        .map(m => m && m.id)
        .filter(Boolean)
        .map(String)
        .filter(k => k.startsWith('DRY_'))
      persistAuthSnapshot(state)
    },

    CLEAR_LOGIN(state) {
      state.loginUser = null
      state.username = ''
      state.externalOrg = null
      state.permissions = []
      state.menus = []
      state.permKeys = []
      state.authButtons = []
      state.forceChangePwd = false
      state.currentUser = null
      clearAuthSnapshot()
    },
    // T-007: 强制改密完成后调用, 解除 forceChangePwd 阻塞
    CLEAR_FORCE_CHANGE_PWD(state) {
      state.forceChangePwd = false
    },

    // ===== T-010A: 写入 getUserPermissionByToken 的返回 =====
    // payload: { menu: [], auth: [], allAuth: [] }
    //   menu  - 用户菜单树 (id=DRY_*, 含 children) — 好粮端 jeecg-system 返回, 不含模块裁剪
    //   auth  - 用户按钮权限 (action 字符串数组)
    //
    // [模块开关] permKeys 的唯一权威来源是 SET_DRYING_MENUS (hl-drying myMenus, 带模块裁剪).
    //   好粮端 getUserPermissionByToken 返回的是未裁剪的完整权限, 会把裁剪结果冲掉.
    //   所以 SET_USER_AUTH 只负责更新按钮级权限 (authButtons), 不再覆盖 permKeys/menus.
    SET_USER_AUTH(state, payload) {
      if (!payload) return
      // auth (按钮权限) 始终更新, 这是另一份独立数据
      const auth = Array.isArray(payload.auth) ? payload.auth : []
      state.authButtons = auth.map(a => a && (a.action || a.perms)).filter(Boolean)
      persistAuthSnapshot(state)
    },

    // ===== 方案A 临时兜底: hl-drying /drying/factory/me 返回 -> externalOrg =====
    // payload: { factoryId, factoryName, orgCode }   外部用户
    //          null                                   内部用户 / 未绑组织 (不覆盖)
    //
    // 映射规则:
    //   factoryId   -> saleOrgId   (前端业务页 computed factoryId() 读这个)
    //   factoryName -> saleOrgName, factoryName
    //
    // 上下文: system-cloud T-002 后端没部署 -> /sys/login 不返回 externalOrg ->
    //         前端拿不到 factoryId -> 业务接口都拦在前端 ("没关联烘干厂").
    //         方案A: hl-drying 新增 /drying/factory/me 接口绕开 system-cloud,
    //         Login.vue 登录成功后异步调用, commit 此 mutation 把数据填进 store.
    //         T-002 部署后此 mutation 可保留兼容 (login 已 SET_LOGIN_INFO 提前赋值, 此处覆盖一致).
    SET_EXTERNAL_ORG(state, payload) {
      if (!payload || !payload.factoryId) return
      state.externalOrg = {
        saleOrgId: payload.factoryId,
        saleOrgName: payload.factoryName,
        factoryName: payload.factoryName,
        orgCode: payload.orgCode
      }
      if (payload.factoryName) state.factoryName = payload.factoryName
      persistAuthSnapshot(state)
    },

    // ===== 字典管理 (DictMgmt.vue 在用) =====
    ADD_DICT_TYPE(state, t) {
      const newId = Math.max(...state.dictTypes.map(x => x.id || 0), 0) + 1
      state.dictTypes.push({ id: newId, ...t })
    },
    UPDATE_DICT_TYPE(state, { id, data }) {
      const idx = state.dictTypes.findIndex(x => x.id === id)
      if (idx >= 0) state.dictTypes.splice(idx, 1, { ...state.dictTypes[idx], ...data })
    },
    DELETE_DICT_TYPE(state, id) {
      const idx = state.dictTypes.findIndex(x => x.id === id)
      if (idx >= 0) {
        const code = state.dictTypes[idx].code
        state.dictTypes.splice(idx, 1)
        state.dictItems = state.dictItems.filter(it => it.typeCode !== code)
      }
    },
    ADD_DICT_ITEM(state, item) {
      const newId = Math.max(...state.dictItems.map(x => x.id || 0), 0) + 1
      state.dictItems.push({ id: newId, sort: 99, default: false, status: 'ACTIVE', ...item })
    },
    UPDATE_DICT_ITEM(state, { id, data }) {
      const idx = state.dictItems.findIndex(x => x.id === id)
      if (idx >= 0) state.dictItems.splice(idx, 1, { ...state.dictItems[idx], ...data })
    },
    DELETE_DICT_ITEM(state, id) {
      const idx = state.dictItems.findIndex(x => x.id === id)
      if (idx >= 0) state.dictItems.splice(idx, 1)
    },

    // [2026-05-30] 海康监控 portal: 当前活跃页 (入库/出库) 的摄像头占位 rect, 让 MainLayout 全局单例对齐
    SET_CAMERA_PORTAL_RECT(state, rect) {
      state.cameraPortalRect = rect ? { ...rect } : null
    },
    SET_SCALE(state, { scale, weight, stable }) {
      state.deviceStatus[scale].currentWeight = weight
      state.deviceStatus[scale].stable = stable
    },
    SET_SCALE_CONNECTED(state, { scale, connected }) {
      state.deviceStatus[scale].connected = connected
    },
    SET_USE_SCALE_TARGET(state, target) {
      state.deviceStatus.useScaleTarget = target === 'tare' ? 'tare' : 'gross'
    },
    // [2026-07-31] 设置磅启用状态 (整体覆盖). payload: { entry: boolean, exit: boolean }
    SET_SCALE_ENABLED(state, { entry, exit }) {
      state.deviceStatus.scaleEnabled = { entry: entry !== false, exit: exit !== false }
    },
    // [2026-08-14] 设置"本机无外设硬件"总开关. payload: boolean
    SET_HARDWARE_DISABLED(state, disabled) {
      state.deviceStatus.hardwareDisabled = !!disabled
    },
    // [2026-10-11] 设置外设服务在线状态. payload: boolean (null 重置为未知)
    SET_PERIPHERAL_SERVICE_ONLINE(state, online) {
      state.deviceStatus.serviceOnline = online === true ? true : (online === false ? false : null)
    },
    // [2026-09-04] UPort 驱动重装弹窗显隐. payload: boolean
    SET_UPORT_REINSTALL_VISIBLE(state, visible) {
      state.deviceStatus.uportReinstallVisible = !!visible
    },

    // ===== 农户 / 种子赊销 (Farmer.vue / Seed.vue 在用) =====
    ADD_FARMER(state, farmer) {
      const newId = Math.max(...state.farmers.map(f => f.id || 0), 0) + 1
      state.farmers.push({ id: newId, phone: '', idCard: '', bankAccount: '', remark: '', ...farmer })
    },
    UPDATE_FARMER(state, { id, data }) {
      const idx = state.farmers.findIndex(f => f.id === id)
      if (idx >= 0) state.farmers.splice(idx, 1, { ...state.farmers[idx], ...data })
    },
    ADD_SEED_SALE(state, sale) {
      const newId = Math.max(...state.seedSales.map(s => s.id || 0), 0) + 1
      const newNo = `ZZ${new Date().toISOString().slice(0, 10).replace(/-/g, '')}-${String(newId).padStart(3, '0')}`
      state.seedSales.push({ id: newId, saleNo: newNo, ...sale })
    }
  }
})

export { nowStr }
